import React, { useState, useEffect } from 'react';
import { FaSearch, FaCheckCircle, FaTimesCircle, FaToggleOn, FaToggleOff, FaEye, FaArrowLeft } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

const AdminUsers = () => {
  const { api } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState({ gender: '', is_verified: '' });

  useEffect(() => {
    fetchUsers();
  }, [page, filter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 20, search };
      if (filter.gender) params.gender = filter.gender;
      if (filter.is_verified !== '') params.is_verified = filter.is_verified;
      const res = await api.get('/api/admin/users', { params });
      if (res.data.success) {
        setUsers(res.data.data.users || res.data.data || []);
        setTotalPages(res.data.data.total_pages || 1);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const toggleStatus = async (userId, currentStatus) => {
    try {
      await api.put(`/api/admin/toggle-user/${userId}`);
      toast.success(`User ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchUsers();
    } catch (err) {
      toast.error('Failed to update user status');
    }
  };

  const verifyProfile = async (userId) => {
    try {
      await api.put(`/api/admin/verify-profile/${userId}`);
      toast.success('Profile verified successfully');
      fetchUsers();
    } catch (err) {
      toast.error('Failed to verify profile');
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-[#FFF8F0] pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <Link to="/admin" className="text-[#8B0000] hover:underline flex items-center gap-1 text-sm mb-2">
                <FaArrowLeft /> Back to Dashboard
              </Link>
              <h1 className="text-3xl font-bold text-[#8B0000] font-['Playfair_Display']">Manage Users</h1>
              <p className="text-gray-600 font-['Noto_Sans_Devanagari']">वापरकर्ते व्यवस्थापन</p>
            </div>
          </div>

          {/* Search & Filters */}
          <div className="bg-white rounded-xl shadow-md p-4 mb-6 flex flex-wrap gap-4 items-end">
            <form onSubmit={handleSearch} className="flex-1 min-w-[200px]">
              <label className="text-sm text-gray-600 block mb-1">Search</label>
              <div className="flex">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Name, email, or mobile..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg focus:outline-none focus:border-[#8B0000]"
                />
                <button type="submit" className="px-4 py-2 bg-[#8B0000] text-white rounded-r-lg hover:bg-[#6B0000]">
                  <FaSearch />
                </button>
              </div>
            </form>
            <div>
              <label className="text-sm text-gray-600 block mb-1">Gender</label>
              <select value={filter.gender} onChange={(e) => { setFilter({...filter, gender: e.target.value}); setPage(1); }} className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#8B0000]">
                <option value="">All</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-600 block mb-1">Verified</label>
              <select value={filter.is_verified} onChange={(e) => { setFilter({...filter, is_verified: e.target.value}); setPage(1); }} className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#8B0000]">
                <option value="">All</option>
                <option value="1">Verified</option>
                <option value="0">Unverified</option>
              </select>
            </div>
          </div>

          {/* Users Table */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#8B0000] text-white">
                  <tr>
                    <th className="py-3 px-4 text-left">ID</th>
                    <th className="py-3 px-4 text-left">Name</th>
                    <th className="py-3 px-4 text-left">Email</th>
                    <th className="py-3 px-4 text-left">Mobile</th>
                    <th className="py-3 px-4 text-left">City</th>
                    <th className="py-3 px-4 text-left">Gender</th>
                    <th className="py-3 px-4 text-left">Role</th>
                    <th className="py-3 px-4 text-left">Verified</th>
                    <th className="py-3 px-4 text-left">Status</th>
                    <th className="py-3 px-4 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="10" className="py-12 text-center text-gray-400">Loading...</td></tr>
                  ) : users.length === 0 ? (
                    <tr><td colSpan="10" className="py-12 text-center text-gray-400">No users found</td></tr>
                  ) : users.map(user => (
                    <tr key={user.id} className="border-b border-gray-100 hover:bg-[#FFF8F0]">
                      <td className="py-3 px-4">{user.id}</td>
                      <td className="py-3 px-4 font-medium">{user.first_name} {user.last_name}</td>
                      <td className="py-3 px-4 text-gray-600">{user.email}</td>
                      <td className="py-3 px-4 text-gray-600">{user.mobile}</td>
                      <td className="py-3 px-4 text-gray-600">{user.city}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${user.gender === 'male' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'}`}>
                          {user.gender}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${user.role === 'admin' ? 'bg-red-100 text-red-700' : user.role === 'premium' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'}`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {user.is_verified ? <FaCheckCircle className="text-green-500" /> : <FaTimesCircle className="text-gray-300" />}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Link to={`/profile/${user.id}`} className="text-blue-600 hover:text-blue-800" title="View Profile">
                            <FaEye />
                          </Link>
                          {!user.is_verified && (
                            <button onClick={() => verifyProfile(user.id)} className="text-green-600 hover:text-green-800" title="Verify">
                              <FaCheckCircle />
                            </button>
                          )}
                          {user.role !== 'admin' && (
                            <button onClick={() => toggleStatus(user.id, user.is_active)} className={user.is_active ? 'text-red-500 hover:text-red-700' : 'text-green-500 hover:text-green-700'} title={user.is_active ? 'Deactivate' : 'Activate'}>
                              {user.is_active ? <FaToggleOn size={18} /> : <FaToggleOff size={18} />}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 p-4 border-t">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 rounded bg-[#8B0000] text-white disabled:opacity-50">Prev</button>
                <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1 rounded bg-[#8B0000] text-white disabled:opacity-50">Next</button>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default AdminUsers;
